import { at, num, f, list, record, values, entries, isArray, array, put } from './support-b1';
import { PhpArray, strval, intval, empty, isset, is_numeric, phpCopy, usort, strcmp, array_merge } from '../../php-compat';
import type { PortEnv, PhpRef } from '../../port-contract';
// A field-presence read must borrow the native row, rather than convert its entire catalog to PHP storage.
const has = (value: object, key: string | number): boolean => value instanceof PhpArray ? value.has(key) : Object.prototype.hasOwnProperty.call(value, key);
interface Deps {
    encounter_row_creature_id(row: unknown): unknown;
    encounter_row_is_dead(row: unknown): boolean;
    inventory_field(row: unknown, key: string): unknown;
    ft_compound_actor_ids(e: unknown, row: unknown): unknown;
    ft_shape_floor_band_for_position(shape: unknown, x: unknown, y: unknown, z: unknown): unknown;
    ft_ground_floor_band_shape_for_position(shape: unknown, x: unknown, y: unknown, z: unknown): unknown;
    ft_ground_occupancy_base_size(row: unknown): unknown;
    ft_ground_with_exterior_open_ground(shape: unknown, x: unknown, y: unknown, x1: unknown, y1: unknown, margin: unknown, canvas: boolean): unknown;
    ft_ground_occupancy_shape(row: unknown, x?: unknown, y?: unknown, z?: unknown): unknown;
    ft_encounter_occupancy_obstacles(e: unknown, row: unknown, ids: unknown): unknown;
    ft_move_resolve_placement(shape: unknown, mover: unknown, obstacles: unknown, z: unknown, base: unknown): unknown;
    ft_move_clearance_radius(mover: unknown): unknown;
    ft_move_occupancy_at(mover: unknown, x: unknown, y: unknown): unknown;
    pos_coord(row: unknown, key: string): unknown;
}
const upper = (v: unknown) => strval(v ?? '').trim().toUpperCase();
/** PHP: ft3d_party_relocation_scalar (includes/functions/janus/encounter/ft3d_party_relocation.php:11) */
export function ft3dPartyRelocationScalar(row: unknown, key: string): unknown { for (const c of [row, ...['encounter_creatures', 'inventory', 'inventories', 'player_characters', 'items', 'item_layer_types'].map(k => at(row, k)).filter(isArray)])
    if (isArray(c) && has(c, key)) {
        const v = at(c, key);
        return isArray(v) && has(v, 0) ? at(v, 0) : v;
    } return null; }
/** PHP: ft3d_room_arrival_anchor (includes/functions/janus/encounter/ft3d_party_relocation.php:27) */
export function ft3dRoomArrivalAnchor(shape: unknown, id: string, rx: unknown, ry: unknown, inset: unknown): unknown {
    rx = f(num(rx));
    ry = f(num(ry));
    inset = f(num(inset));
    const room = values(at(shape, 'boxes')).find(b => isArray(b) && strval(at(b, 'id') ?? '') === id);
    if (!isArray(room))
        return null;
    const xmin = num(at(room, 'x', 'min') ?? 0), xmax = num(at(room, 'x', 'max') ?? 0), ymin = num(at(room, 'y', 'min') ?? 0), ymax = num(at(room, 'y', 'max') ?? 0), floor = num(at(room, 'floor') ?? 0), ceiling = num(at(room, 'ceiling') ?? floor), eps = 1e-5;
    if (xmax <= xmin || ymax <= ymin || ceiling <= floor)
        return null;
    const candidates = [];
    for (const o of values(at(shape, 'openings'))) {
        if (!isArray(o) || at(o, 'passable') === false)
            continue;
        const rooms = at(o, 'creation', 'rooms');
        if (!isArray(rooms) || !values(rooms).map(v => strval(v)).includes(id))
            continue;
        const axis = strval(at(o, 'plane', 'axis') ?? ''), raw = at(o, 'plane', 'at'), lo = at(o, 'span', 'min'), hi = at(o, 'span', 'max'), zlo = at(o, 'z', 'min'), zhi = at(o, 'z', 'max');
        if (!['x', 'y'].includes(axis) || ![raw, lo, hi, zlo, zhi].every(is_numeric) || num(hi) <= num(lo) || Math.min(num(zhi), ceiling) <= Math.max(num(zlo), floor) + eps)
            continue;
        const pos = num(raw), mid = (num(lo) + num(hi)) / 2;
        let x: number, y: number, face: string;
        if (axis === 'x' && Math.abs(pos - xmin) <= eps) {
            x = xmin + num(inset);
            y = mid;
            face = 'x-min';
        }
        else if (axis === 'x' && Math.abs(pos - xmax) <= eps) {
            x = xmax - num(inset);
            y = mid;
            face = 'x-max';
        }
        else if (axis === 'y' && Math.abs(pos - ymin) <= eps) {
            x = mid;
            y = ymin + num(inset);
            face = 'y-min';
        }
        else if (axis === 'y' && Math.abs(pos - ymax) <= eps) {
            x = mid;
            y = ymax - num(inset);
            face = 'y-max';
        }
        else
            continue;
        if (x < xmin - eps || x > xmax + eps || y < ymin - eps || y > ymax + eps)
            continue;
        candidates.push(record({ opening: o, x: f(x), y: f(y), face, distance: f(Math.hypot(x - num(rx), y - num(ry))) }));
    }
    const sorted = values(usort(list(candidates), (a, b) => { const d = num(at(a, 'distance')) < num(at(b, 'distance')) ? -1 : num(at(a, 'distance')) > num(at(b, 'distance')) ? 1 : 0; return d !== 0 ? d : strcmp(strval(at(a, 'opening', 'id') ?? ''), strval(at(b, 'opening', 'id') ?? '')); }));
    return sorted.length ? record({ room, opening: at(sorted[0], 'opening'), x: f(num(at(sorted[0], 'x'))), y: f(num(at(sorted[0], 'y'))), source: 'nearest-passable-opening' }) : record({ room, opening: null, x: f((xmin + xmax) / 2), y: f((ymin + ymax) / 2), source: 'safe-center-fallback' });
}
function rowMaps(e: unknown, env: PortEnv<Deps>, onCreature?: (id: string, row: unknown) => void): [
    Map<string, unknown>,
    Map<string, unknown>
] { const creatures = new Map<string, unknown>(), inventories = new Map<string, unknown>(); for (const row of values(at(e, 'encounter_creatures_arr'))) {
    if (!isArray(row))
        continue;
    const id = strval(env.deps.encounter_row_creature_id(row) ?? '');
    if (id !== '') {
        creatures.set(id, row);
        onCreature?.(id, row);
    }
} for (const row of values(at(e, 'encounter_inventories_arr'))) {
    if (!isArray(row))
        continue;
    const id = strval(env.deps.inventory_field(row, 'inventory_id') ?? '');
    if (id !== '')
        inventories.set(id, row);
} return [creatures, inventories]; }
function selectedGroups(e: unknown, creatures: Map<string, unknown>, inventories: Map<string, unknown>, selected: Set<string>, env: PortEnv<Deps>): unknown {
    let changed = true;
    while (changed) {
        changed = false;
        for (const [id, row] of creatures) {
            const controller = strval(ft3dPartyRelocationScalar(row, 'controller_encounter_creature_id') ?? '');
            if (controller !== '' && selected.has(controller) && !selected.has(id) && !env.deps.encounter_row_is_dead(row)) {
                selected.add(id);
                changed = true;
            }
        }
    }
    const all = new Map([...inventories, ...creatures]), selectedIds = new Set(selected);
    for (const id of selected)
        for (const [cid] of entries(env.deps.ft_compound_actor_ids(e, creatures.get(id))))
            if (all.has(strval(cid)))
                selectedIds.add(strval(cid));
    const seen = new Set<string>(), groups = [];
    for (const seed of [...selected].sort(strcmp)) {
        if (seen.has(seed))
            continue;
        const ids = record({});
        for (const [id] of entries(env.deps.ft_compound_actor_ids(e, creatures.get(seed))))
            if (selectedIds.has(strval(id)) && all.has(strval(id)))
                ids.set(id, true);
        if (empty(ids))
            ids.set(seed, true);
        const rows = [];
        for (const [id] of entries(ids)) {
            seen.add(strval(id));
            rows.push(all.get(strval(id)));
        }
        groups.push(record({ ids, rows: list(rows) }));
    }
    return list(groups);
}
/** PHP: ft3d_party_relocation_groups (includes/functions/janus/encounter/ft3d_party_relocation.php:118) */
export function ft3dPartyRelocationGroups(campaign: unknown, e: unknown, env: PortEnv<Deps>): unknown {
    const active = new Set<string>();
    for (const pc of values(at(campaign, 'campaign_players'))) {
        if (!isArray(pc) || Number(intval(ft3dPartyRelocationScalar(pc, 'player_character_inactive') ?? 0)) === 1)
            continue;
        const id = upper(ft3dPartyRelocationScalar(pc, 'item_id'));
        if (id !== '')
            active.add(id);
    }
    const selected = new Set<string>();
    const [creatures, inventories] = rowMaps(e, env, (id, row) => {
        if (active.has(upper(ft3dPartyRelocationScalar(row, 'item_id'))) && !env.deps.encounter_row_is_dead(row)) selected.add(id);
    });
    return selectedGroups(e, creatures, inventories, selected, env);
}
/** PHP: ft3d_selection_relocation_groups (includes/functions/janus/encounter/ft3d_party_relocation.php:649) */
export function ft3dSelectionRelocationGroups(e: unknown, seeds: unknown, env: PortEnv<Deps>): unknown { const [creatures, inventories] = rowMaps(e, env), selected = new Set<string>(); for (const [id] of creatures)
    if (isset(at(seeds, upper(id))))
        selected.add(id); return selectedGroups(e, creatures, inventories, selected, env); }
/** PHP: ft3d_party_member_relocation_group (includes/functions/janus/encounter/ft3d_party_relocation.php:194) */
export function ft3dPartyMemberRelocationGroup(campaign: unknown, e: unknown, item: string, env: PortEnv<Deps>): unknown {
    const wanted = upper(item);
    if (wanted === '')
        return null;
    const roster = values(at(campaign, 'campaign_players')).find(pc => isArray(pc) && upper(ft3dPartyRelocationScalar(pc, 'item_id')) === wanted);
    if (!isArray(roster))
        return null;
    let seed: unknown = null;
    const [creatures, inventories] = rowMaps(e, env, (_id, row) => {
        if (upper(ft3dPartyRelocationScalar(row, 'item_id')) === wanted)
            seed = row;
    });
    if (!isArray(seed))
        return null;
    const sid = strval(env.deps.encounter_row_creature_id(seed) ?? ''), ids = phpCopy(array(env.deps.ft_compound_actor_ids(e, seed)));
    if (empty(ids))
        ids.set(sid, true);
    const all = new Map([...inventories, ...creatures]), rows = [];
    for (const [id] of entries(ids))
        if (all.has(strval(id)))
            rows.push(all.get(strval(id)));
    if (!rows.length)
        rows.push(seed);
    return record({ playerItemId: wanted, creatureId: sid, rows: list(rows), ids });
}
/** PHP: ft3d_plan_party_member_group_relocation (includes/functions/janus/encounter/ft3d_party_relocation.php:255) */
export function ft3dPlanPartyMemberGroupRelocation(group: unknown, e: unknown, shape: unknown, x: unknown, y: unknown, z: unknown, env: PortEnv<Deps>): unknown {
    x = f(num(x));
    y = f(num(y));
    z = f(num(z));
    const floor = env.deps.ft_shape_floor_band_for_position(shape, x, y, z), projection = env.deps.ft_ground_floor_band_shape_for_position(shape, x, y, z);
    let floorShape = isArray(projection) ? at(projection, 'shape') : array_merge(array(shape), record({ boxes: list([]), openings: list([]) }));
    const rows = values(at(group, 'rows'));
    let rep = rows[0];
    for (const row of rows)
        if (num(env.deps.ft_ground_occupancy_base_size(row)) > num(env.deps.ft_ground_occupancy_base_size(rep)))
            rep = row;
    const base = env.deps.ft_ground_occupancy_base_size(rep), blocked = (reason: unknown) => record({ status: 'blocked', reason, playerItemId: at(group, 'playerItemId'), creatureId: at(group, 'creatureId') });
    if (Math.abs(num(floor)) < 1e-6) {
        const canvas = at(shape, 'authoring', 'canvas');
        if (isArray(canvas) && ['x', 'y'].every(a => is_numeric(at(canvas, a, 'min')) && is_numeric(at(canvas, a, 'max'))) && (num(x) < num(at(canvas, 'x', 'min')) || num(x) > num(at(canvas, 'x', 'max')) || num(y) < num(at(canvas, 'y', 'min')) || num(y) > num(at(canvas, 'y', 'max'))))
            return blocked('outside_union');
        const margin = f(40 + num(base) * 2);
        floorShape = !empty(at(floorShape, 'boxes')) ? env.deps.ft_ground_with_exterior_open_ground(floorShape, x, y, x, y, margin, true) : array_merge(array(floorShape), record({ boxes: list([record({ id: 'navigation-open-ground', x: record({ min: f(num(x) - num(margin)), max: f(num(x) + num(margin)) }), y: record({ min: f(num(y) - num(margin)), max: f(num(y) + num(margin)) }), floor: f(0), ceiling: f(1000), navigationOpenGround: true })]), openings: list([]) }));
    }
    const mover = env.deps.ft_ground_occupancy_shape(rep, x, y, f(num(z) + num(base) / 2));
    if (mover === null)
        return blocked('no_footprint');
    const obstacles = env.deps.ft_encounter_occupancy_obstacles(e, rep, at(group, 'ids')), result = env.deps.ft_move_resolve_placement(floorShape, mover, obstacles, f(num(z) + .5), base);
    if ((at(result, 'status') ?? '') !== 'placed')
        return blocked(strval(at(result, 'reason') ?? 'destination_blocked'));
    z = f(num(at(result, 'z') ?? z));
    const resolvedFloor = env.deps.ft_shape_floor_band_for_position(shape, f(num(at(result, 'x'))), f(num(at(result, 'y'))), z);
    if (Math.abs(num(resolvedFloor) - num(floor)) > .01)
        return blocked('destination_floor_mismatch');
    const placements = [];
    for (const row of rows) {
        const cid = strval(env.deps.encounter_row_creature_id(row) ?? ''), iid = strval(env.deps.inventory_field(row, 'inventory_id') ?? ''), id = cid !== '' ? cid : iid;
        if (id === '')
            continue;
        placements.push(record({ id, type: cid !== '' ? 'creature' : 'inventory', x: f(num(at(result, 'x'))), y: f(num(at(result, 'y'))), z }));
    }
    return record({ status: 'placed', playerItemId: at(group, 'playerItemId'), creatureId: at(group, 'creatureId'), destination: record({ x: f(num(at(result, 'x'))), y: f(num(at(result, 'y'))), z, adjusted: !empty(at(result, 'moved') ?? false) }), placements: list(placements) });
}
/** PHP: ft3d_plan_party_member_relocation (includes/functions/janus/encounter/ft3d_party_relocation.php:397) */
export function ft3dPlanPartyMemberRelocation(c: unknown, e: unknown, shape: unknown, item: string, x: unknown, y: unknown, z: unknown, env: PortEnv<Deps>): unknown {
    x = f(num(x));
    y = f(num(y));
    z = f(num(z));
    const group = ft3dPartyMemberRelocationGroup(c, e, item, env);
    return group === null ? record({ status: 'blocked', reason: 'party_member_not_in_current_encounter', playerItemId: item.toUpperCase() }) : ft3dPlanPartyMemberGroupRelocation(group, e, shape, x, y, z, env);
}
/** PHP: ft3d_plan_party_member_transfer (includes/functions/janus/encounter/ft3d_party_relocation.php:428) */
export function ft3dPlanPartyMemberTransfer(c: unknown, source: unknown, dest: unknown, shape: unknown, item: string, x: unknown, y: unknown, z: unknown, env: PortEnv<Deps>): unknown {
    x = f(num(x));
    y = f(num(y));
    z = f(num(z));
    const group = ft3dPartyMemberRelocationGroup(c, source, item, env);
    return group === null ? record({ status: 'blocked', reason: 'party_member_not_in_source_encounter', playerItemId: item.toUpperCase() }) : ft3dPlanPartyMemberGroupRelocation(group, dest, shape, x, y, z, env);
}
/** PHP: ft3d_mirror_placement_onto_roster (includes/functions/janus/encounter/ft3d_party_relocation.php:595) */
export function ft3dMirrorPlacementOntoRoster(e: PhpRef<unknown>, placement: unknown, env: PortEnv<Deps>): null {
    const id = upper(at(placement, 'id')), bucket = (at(placement, 'type') ?? 'creature') === 'creature' ? 'encounter_creatures_arr' : 'encounter_inventories_arr';
    for (const [i, row] of entries(at(e.value, bucket))) {
        if (!isArray(row))
            continue;
        const rid = bucket === 'encounter_creatures_arr' ? strval(env.deps.encounter_row_creature_id(row) ?? '') : strval(env.deps.inventory_field(row, 'inventory_id') ?? '');
        if (upper(rid) !== id)
            continue;
        for (const axis of ['x', 'y', 'z']) {
            const col = axis + '_pos', v = at(placement, axis);
            put(e.value, [bucket, i, col], v);
            for (const b of ['encounter_creatures', 'inventory'])
                if (isset(at(e.value, bucket, i, b)))
                    put(e.value, [bucket, i, b, col], v);
        }
        return null;
    }
    return null;
}
/** PHP: ft3d_room_contains_occupancy (includes/functions/janus/encounter/ft3d_party_relocation.php:619) */
export function ft3dRoomContainsOccupancy(room: unknown, occupancy: unknown, env: PortEnv<Deps>): boolean { const radius = num(env.deps.ft_move_clearance_radius(occupancy)), x = num(at(occupancy, 'x')), y = num(at(occupancy, 'y')); return x - radius >= num(at(room, 'x', 'min')) - 1e-6 && x + radius <= num(at(room, 'x', 'max')) + 1e-6 && y - radius >= num(at(room, 'y', 'min')) - 1e-6 && y + radius <= num(at(room, 'y', 'max')) + 1e-6; }
/** PHP: ft3d_plan_party_relocation (includes/functions/janus/encounter/ft3d_party_relocation.php:628) */
export function ft3dPlanPartyRelocation(c: unknown, e: unknown, shape: unknown, id: string, env: PortEnv<Deps>): unknown { const groups = ft3dPartyRelocationGroups(c, e, env); return empty(groups) ? record({ status: 'blocked', reason: 'no_active_party', roomId: id }) : ft3dPlanGroupsIntoRoom(groups, e, shape, id, null, env); }
/** PHP: ft3d_plan_companion_arrivals (includes/functions/janus/encounter/ft3d_party_relocation.php:478) */
export function ft3dPlanCompanionArrivals(e: unknown, shape: unknown, dest: unknown, planned: unknown, before: unknown, item: string, env: PortEnv<Deps>): unknown {
    const rows = new Map<string, unknown>(), types = new Map<string, string>(), arrived = record({});
    for (const kind of ['creature', 'inventory'])
        for (const row of values(at(e, kind === 'creature' ? 'encounter_creatures_arr' : 'encounter_inventories_arr'))) {
            if (!isArray(row))
                continue;
            const id = upper(kind === 'creature' ? env.deps.encounter_row_creature_id(row) : env.deps.inventory_field(row, 'inventory_id'));
            if (id === '' || isset(at(planned, id)) || isset(at(before, id)))
                continue;
            rows.set(id, row);
            types.set(id, kind);
            if (kind === 'creature')
                arrived.set(id, true);
        }
    const placements = [], diagnostics = [], handled = new Set<string>(), working = { value: phpCopy(e) };
    for (const id of [...rows.keys()].sort(strcmp)) {
        if (handled.has(id))
            continue;
        const seed = rows.get(id), ids = record({});
        if (types.get(id) === 'creature')
            for (const [cid] of entries(env.deps.ft_compound_actor_ids(working.value, seed))) {
                const k = upper(cid);
                if (rows.has(k))
                    ids.set(k, true);
            }
        if (empty(ids))
            ids.set(id, true);
        const groupRows = [];
        for (const [k] of entries(ids)) {
            handled.add(strval(k));
            groupRows.push(rows.get(strval(k)));
        }
        const plan = ft3dPlanPartyMemberGroupRelocation(record({ playerItemId: item, creatureId: id, ids, rows: list(groupRows) }), working.value, shape, f(num(at(dest, 'x'))), f(num(at(dest, 'y'))), f(num(at(dest, 'z'))), env);
        if ((at(plan, 'status') ?? '') !== 'placed') {
            diagnostics.push(record({ id, issue: 'companion_not_placed', reason: strval(at(plan, 'reason') ?? 'unknown') }));
            continue;
        }
        const planIds = [...new Set(values(at(plan, 'placements')).map(p => upper(at(p, 'id'))))], expected = entries(ids).map(([k]) => strval(k));
        if (expected.some(k => !planIds.includes(k)) || planIds.some(k => !expected.includes(k))) {
            diagnostics.push(record({ id, issue: 'companion_plan_incomplete', planned: planIds.join(','), expected: expected.join(',') }));
            continue;
        }
        for (const p of values(at(plan, 'placements'))) {
            placements.push(p);
            ft3dMirrorPlacementOntoRoster(working, p, env);
        }
    }
    return record({ arrivedCreatureIds: arrived, placements: list(placements), diagnostics: list(diagnostics) });
}
/** Entry first, then opening-connected rooms by breadth and distance, then other authored floors. */
function arrivalRooms(shape: unknown, anchor: unknown): unknown[] {
    const first = at(anchor, 'room'), rooms = values(at(shape, 'boxes')).filter(r =>
        num(at(r, 'x', 'max')) > num(at(r, 'x', 'min')) && num(at(r, 'y', 'max')) > num(at(r, 'y', 'min')) && num(at(r, 'ceiling')) > num(at(r, 'floor')));
    const distance = (r: unknown): number => Math.hypot(
        Math.max(num(at(r, 'x', 'min')) - num(at(anchor, 'x')), 0, num(at(anchor, 'x')) - num(at(r, 'x', 'max'))),
        Math.max(num(at(r, 'y', 'min')) - num(at(anchor, 'y')), 0, num(at(anchor, 'y')) - num(at(r, 'y', 'max'))));
    const byDistance = (a: unknown, b: unknown): number => distance(a) - distance(b) || strcmp(strval(at(a, 'id')), strval(at(b, 'id')));
    const links = new Map<unknown, Set<unknown>>(rooms.map(r => [r, new Set()]));
    for (const opening of values(at(shape, 'openings'))) {
        if (at(opening, 'passable') === false) continue;
        const axis = at(opening, 'plane', 'axis');
        if (axis !== 'x' && axis !== 'y') continue;
        const other = axis === 'x' ? 'y' : 'x', plane = num(at(opening, 'plane', 'at'));
        // Older authored openings have no creation.rooms; their physical face/span is authoritative too.
        const touching = rooms.filter(r => (Math.abs(num(at(r, axis, 'min')) - plane) < 1e-5 || Math.abs(num(at(r, axis, 'max')) - plane) < 1e-5)
            && Math.min(num(at(r, other, 'max')), num(at(opening, 'span', 'max'))) > Math.max(num(at(r, other, 'min')), num(at(opening, 'span', 'min'))) + 1e-5
            && Math.min(num(at(r, 'ceiling')), num(at(opening, 'z', 'max'))) > Math.max(num(at(r, 'floor')), num(at(opening, 'z', 'min'))) + 1e-5);
        for (const a of touching) for (const b of touching) if (a !== b) links.get(a)!.add(b);
    }
    const ordered = [first], seen = new Set([first]);
    let frontier = [first];
    while (frontier.length) {
        const next = [...new Set(frontier.flatMap(r => [...(links.get(r) ?? [])]))].filter(r => !seen.has(r)).sort(byDistance);
        for (const r of next) { seen.add(r); ordered.push(r); }
        frontier = next;
    }
    return ordered.concat(rooms.filter(r => !seen.has(r)).sort(byDistance));
}

/** Fine, near-anchor-first samples, including room edges when the lattice does not land on them. */
function* arrivalCandidates(room: unknown, x: number, y: number, radius: number): Generator<[number, number]> {
    const xmin = num(at(room, 'x', 'min')) + radius, xmax = num(at(room, 'x', 'max')) - radius;
    const ymin = num(at(room, 'y', 'min')) + radius, ymax = num(at(room, 'y', 'max')) - radius;
    if (xmax < xmin || ymax < ymin) return;
    x = Math.max(xmin, Math.min(xmax, x)); y = Math.max(ymin, Math.min(ymax, y));
    const step = Math.min(2.5, Math.max(.01, radius)), seen = new Set<string>();
    const sample = function* (cx: number, cy: number): Generator<[number, number]> {
        cx = Math.max(xmin, Math.min(xmax, cx)); cy = Math.max(ymin, Math.min(ymax, cy));
        const key = `${cx.toFixed(6)},${cy.toFixed(6)}`;
        if (!seen.has(key)) { seen.add(key); yield [cx, cy]; }
    };
    yield* sample(x, y);
    const rings = Math.ceil(Math.max(x - xmin, xmax - x, y - ymin, ymax - y) / step);
    for (let ring = 1; ring <= Math.min(128, rings); ring++) {
        // Visit the perimeter only; do not scan the ring's entire filled square.
        for (let dx = -ring; dx <= ring; dx++) { yield* sample(x + dx * step, y - ring * step); yield* sample(x + dx * step, y + ring * step); }
        for (let dy = -ring + 1; dy < ring; dy++) { yield* sample(x - ring * step, y + dy * step); yield* sample(x + ring * step, y + dy * step); }
    }
    yield* sample((xmin + xmax) / 2, (ymin + ymax) / 2);
}

export function ft3dPlanGroupsIntoRoom(groups: unknown, e: unknown, shape: unknown, id: string, reference: unknown = null, env: PortEnv<Deps>): unknown {
    if (empty(groups))
        return record({ status: 'blocked', reason: 'nothing_to_move', roomId: id });
    const departed = record({});
    let rx = 0, ry = 0, count = 0, radius = 2.5;
    for (const g of values(groups)) {
        for (const row of values(at(g, 'rows'))) {
            rx += num(env.deps.pos_coord(row, 'x_pos') ?? 0);
            ry += num(env.deps.pos_coord(row, 'y_pos') ?? 0);
            count++;
            radius = Math.max(radius, num(env.deps.ft_move_clearance_radius(env.deps.ft_ground_occupancy_shape(row))));
        }
    }
    if (reference !== null) {
        rx = num(at(reference, 'x'));
        ry = num(at(reference, 'y'));
    }
    else if (count > 0) {
        rx /= count;
        ry /= count;
    }
    const anchor = ft3dRoomArrivalAnchor(shape, id, f(rx), f(ry), f(radius + .5));
    if (anchor === null)
        return record({ status: 'blocked', reason: 'room_not_found', roomId: id });
    const rooms = arrivalRooms(shape, anchor), accepted: unknown[] = [], placements = [], budgets = new Map<unknown, number>();
    let blockedGroups: unknown[] = [], pending = entries(groups);
    do {
      const rejected: typeof pending = [], beforeCount = placements.length;
      blockedGroups = [];
      for (const [i, g] of pending) {
        const rows = values(at(g, 'rows'));
        let rep = rows[0];
        for (const row of rows)
            if (num(env.deps.ft_move_clearance_radius(env.deps.ft_ground_occupancy_shape(row))) > num(env.deps.ft_move_clearance_radius(env.deps.ft_ground_occupancy_shape(rep))))
                rep = row;
        const base = env.deps.ft_ground_occupancy_base_size(rep), obstacles = array_merge(array(env.deps.ft_encounter_occupancy_obstacles(e, rep, array_merge(departed, array(at(g, 'ids'))))), list(accepted));
        let resolved: unknown = null, floor = 0, attempts = budgets.get(i) ?? 0, limited = false;
        for (const room of rooms) {
          const dz = f(num(at(room, 'floor')) + num(base) / 2), inset = num(env.deps.ft_move_clearance_radius(env.deps.ft_ground_occupancy_shape(rep)));
          const local = room === at(anchor, 'room') ? anchor : ft3dRoomArrivalAnchor(shape, strval(at(room, 'id')), at(anchor, 'x'), at(anchor, 'y'), f(inset + .5));
          if (local === null) continue;
          if (Math.max(num(at(room, 'x', 'max')) - num(at(room, 'x', 'min')), num(at(room, 'y', 'max')) - num(at(room, 'y', 'min'))) > 128 * Math.min(2.5, Math.max(.01, inset))) limited = true;
          let roomAttempts = 0;
          for (const c of arrivalCandidates(room, num(at(local, 'x')), num(at(local, 'y')), inset)) {
            // A budget refusal must never claim that a destination was proven full.
            if (++roomAttempts > 16384 || ++attempts > 65536) { limited = true; break; }
            const mover = env.deps.ft_ground_occupancy_shape(rep, f(c[0]!), f(c[1]!), dz);
            if (mover === null || !ft3dRoomContainsOccupancy(room, mover, env))
                continue;
            const result = env.deps.ft_move_resolve_placement(shape, mover, obstacles, f((num(at(mover, 'zMin')) + num(at(mover, 'zMax'))) / 2), base);
            if ((at(result, 'status') ?? '') !== 'placed')
                continue;
            const final = env.deps.ft_move_occupancy_at(mover, f(num(at(result, 'x'))), f(num(at(result, 'y'))));
            if (!ft3dRoomContainsOccupancy(room, final, env))
                continue;
            resolved = record({ x: f(num(at(result, 'x'))), y: f(num(at(result, 'y'))) });
            floor = num(at(room, 'floor'));
            break;
          }
          if (resolved !== null || attempts > 65536) break;
        }
        if (resolved === null) {
            blockedGroups.push(record({ index: i, reason: limited ? 'placement_search_limit' : 'formation_blocked' }));
            budgets.set(i, attempts);
            rejected.push([i, g]);
            continue;
        }
        for (const [key] of entries(at(g, 'ids'))) departed.set(key, true);
        for (const row of rows) {
            const cid = strval(env.deps.encounter_row_creature_id(row) ?? ''), iid = strval(env.deps.inventory_field(row, 'inventory_id') ?? ''), type = cid !== '' ? 'creature' : 'inventory', rid = cid !== '' ? cid : iid, memberBase = env.deps.ft_ground_occupancy_base_size(row), mz = f(floor), cz = f(num(mz) + num(memberBase) / 2);
            const zone = env.deps.ft_ground_occupancy_shape(row, at(resolved, 'x'), at(resolved, 'y'), cz);
            if (zone !== null)
                accepted.push(zone);
            placements.push(record({ id: rid, type, x: at(resolved, 'x'), y: at(resolved, 'y'), z: mz }));
        }
      }
      // A later same-encounter departure can free a previously blocked group's old room.
      pending = rejected;
      if (placements.length === beforeCount) break;
    } while (pending.length);
    return record({ status: placements.length ? 'placed' : 'blocked', ...(placements.length ? {} : { reason: at(blockedGroups[0], 'reason'), blockedGroup: at(blockedGroups[0], 'index') }),
        blockedGroups: list(blockedGroups), roomId: id, anchor: record({ source: at(anchor, 'source'), openingId: at(anchor, 'opening', 'id'), x: at(anchor, 'x'), y: at(anchor, 'y') }), placements: list(placements) });
}
